export const CURTAIN_ATTRIBUTE_SIZE = 6;
export const CURTAIN_IMPACT_LIMIT = 3;
export const CURTAIN_IMPACT_DURATION_MS = 1_800;
export const CURTAIN_SUCCESS_DURATION_MS = 300;
export const CURTAIN_SUCCESS_HARD_LIMIT_MS = 350;

export interface CurtainProfile {
  particleCount: number;
  targetFps: number;
  dprCap: number;
}

export interface CurtainImpact {
  x: number;
  y: number;
  startedAt: number;
  strength: number;
}

export interface CurtainBounds {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function curtainProfileForWidth(width: number): CurtainProfile {
  if (width >= 1_200) return { particleCount: 36_000, targetFps: 60, dprCap: 1.5 };
  if (width >= 720) return { particleCount: 12_000, targetFps: 45, dprCap: 1.35 };
  return {
    particleCount: width <= 380 ? 1_000 : 1_200,
    targetFps: 30,
    dprCap: 1.25,
  };
}

function seededRandom(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function curtainSpine(t: number) {
  const inverse = 1 - t;
  const inverseSquared = inverse * inverse;
  const tSquared = t * t;
  return {
    x: inverseSquared * inverse * -0.08
      + 3 * inverseSquared * t * -0.82
      + 3 * inverse * tSquared * -0.78
      + tSquared * t * 0.68,
    y: inverseSquared * inverse * 1.12
      + 3 * inverseSquared * t * 0.74
      + 3 * inverse * tSquared * -0.46
      + tSquared * t * -1.08,
  };
}

function curtainTangent(t: number) {
  const inverse = 1 - t;
  const x = 3 * inverse * inverse * (-0.82 + 0.08)
    + 6 * inverse * t * (-0.78 + 0.82)
    + 3 * t * t * (0.68 + 0.78);
  const y = 3 * inverse * inverse * (0.74 - 1.12)
    + 6 * inverse * t * (-0.46 - 0.74)
    + 3 * t * t * (-1.08 + 0.46);
  const length = Math.hypot(x, y) || 1;
  return { x: x / length, y: y / length };
}

function fallsInsideCurtainHole(t: number, across: number) {
  const central = ((t - 0.49) / 0.055) ** 2 + ((across - 0.52) / 0.16) ** 2;
  const lower = ((t - 0.73) / 0.055) ** 2 + ((across + 0.58) / 0.13) ** 2;
  return central < 1 || lower < 1;
}

export function createCurtainParticleData(count: number, seed = 0x50545054) {
  const random = seededRandom(seed);
  const values = new Float32Array(count * CURTAIN_ATTRIBUTE_SIZE);
  let written = 0;
  let attempts = 0;

  while (written < count) {
    attempts += 1;
    if (attempts > count * 24) throw new Error(`Unable to generate ${count} curtain particles`);

    const halo = random() < 0.28;
    const t = random();
    const ridgeWeighted = !halo && random() < 0.56;
    const across = halo
      ? (random() * 2 - 1) * 1.42
      : ridgeWeighted
        ? ((random() + random() + random()) / 3 * 2 - 1) * 0.96
        : random() * 2 - 1;
    const localDensity = 0.78
      + Math.sin(t * 17.4 + across * 3.1) * 0.12
      + Math.cos(t * 8.8 - across * 5.6) * 0.1
      - Math.max(0, Math.abs(across) - 0.82) * 0.18;
    if (random() > localDensity) continue;
    if (fallsInsideCurtainHole(t, across) && random() < 0.93) continue;

    const center = curtainSpine(t);
    const tangent = curtainTangent(t);
    const normal = { x: -tangent.y, y: tangent.x };
    const width = (0.74 + Math.sin(t * 9.1 + 0.65) * 0.085)
      * (halo ? 1.18 : 1);
    const twist = -0.42 + t * 4.65 + Math.sin(t * 7.2) * 0.22;
    const lateral = across * width;
    const projectedWidth = lateral * (0.9 + 0.1 * Math.cos(twist));
    const centralFold = Math.exp(-Math.pow((t - 0.5) / 0.18, 2));
    const foldPhase = across * Math.PI * 2.25 + t * 8.2;
    const foldAlong = Math.sin(foldPhase) * centralFold * (0.085 + (1 - Math.abs(across)) * 0.045);
    const ridge = Math.exp(-Math.pow((across + 0.02) / 0.42, 2));
    let depth = ridge * 0.75
      - Math.abs(across) * 0.2
      + Math.sin(twist + across * 2.4) * 0.13
      + Math.cos(foldPhase) * centralFold * 0.1
      + (random() - 0.5) * (halo ? 0.19 : 0.065);
    const alongJitter = (random() - 0.5) * (halo ? 0.15 : 0.038);
    const edgeFray = Math.abs(across) > 0.8 ? (random() - 0.5) * 0.1 : 0;
    let x = center.x
      + normal.x * (projectedWidth + edgeFray)
      + tangent.x * (alongJitter + foldAlong);
    let y = center.y
      + normal.y * (projectedWidth + edgeFray) * 0.92
      + tangent.y * (alongJitter + foldAlong);
    if (y > 0.72 && x < -0.46 && random() < 0.94) continue;
    const tunnelX = -0.18;
    const tunnelY = 0.07;
    const tunnelDeltaX = x - tunnelX;
    const tunnelDeltaY = (y - tunnelY) / 0.82;
    const tunnelDistance = Math.hypot(tunnelDeltaX, tunnelDeltaY);
    if (tunnelDistance < 0.17 && random() < 0.96) continue;
    if (tunnelDistance < 0.46) {
      const tunnelInfluence = (1 - tunnelDistance / 0.46) * centralFold;
      const tunnelAngle = Math.atan2(tunnelDeltaY, tunnelDeltaX) + tunnelInfluence * 0.84;
      x = tunnelX + Math.cos(tunnelAngle) * tunnelDistance;
      y = tunnelY + Math.sin(tunnelAngle) * tunnelDistance * 0.82;
      const innerFold = Math.exp(-Math.pow((tunnelDistance - 0.24) / 0.11, 2));
      const outerFold = Math.exp(-Math.pow((tunnelDistance - 0.39) / 0.15, 2));
      depth += (innerFold * 0.34 + outerFold * 0.14) * centralFold;
    }
    const size = halo
      ? 0.27 + random() ** 1.9 * 0.4
      : 0.45 + random() ** 1.5 * 0.62;

    const offset = written * CURTAIN_ATTRIBUTE_SIZE;
    values[offset] = x;
    values[offset + 1] = y;
    values[offset + 2] = depth;
    values[offset + 3] = random();
    values[offset + 4] = size;
    values[offset + 5] = random() < 0.018 ? 1 : 0;
    written += 1;
  }

  return values;
}

export function appendCurtainImpact(
  impacts: readonly CurtainImpact[],
  impact: CurtainImpact,
) {
  return [...impacts, impact].slice(-CURTAIN_IMPACT_LIMIT);
}

export function activeCurtainImpacts(
  impacts: readonly CurtainImpact[],
  now: number,
) {
  return impacts.filter((impact) => now - impact.startedAt < CURTAIN_IMPACT_DURATION_MS);
}

export function pointerInCurtainBounds(
  clientX: number,
  clientY: number,
  bounds: CurtainBounds,
) {
  return bounds.width > 0
    && bounds.height > 0
    && clientX >= bounds.left
    && clientX <= bounds.left + bounds.width
    && clientY >= bounds.top
    && clientY <= bounds.top + bounds.height;
}

export function normalizeCurtainPointer(
  clientX: number,
  clientY: number,
  bounds: CurtainBounds,
) {
  return {
    x: ((clientX - bounds.left) / bounds.width) * 2 - 1,
    y: 1 - ((clientY - bounds.top) / bounds.height) * 2,
  };
}

export function curtainSuccessDelay(reducedMotion: boolean) {
  return reducedMotion ? 0 : CURTAIN_SUCCESS_DURATION_MS;
}
